import { type QueryOptions } from '@apollo/client';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import { subYears } from 'date-fns';
import {
  ArrowLeftRight,
  Bell,
  BookOpen,
  Bot,
  Building,
  Building2,
  CalendarDays,
  ChartColumn,
  ChartLine,
  Code,
  Compass,
  Construction,
  CreditCard,
  Eye,
  FileChartColumn,
  FileStack,
  FileText,
  FlaskConical,
  Gauge,
  Globe,
  GraduationCap,
  HandCoins,
  Handshake,
  House,
  Inbox,
  Landmark,
  ListChecks,
  type LucideIcon,
  Magnet,
  Mail,
  Map,
  Megaphone,
  MessagesSquare,
  Network,
  NotebookText,
  Receipt,
  Repeat,
  Scale,
  Search,
  Send,
  Settings,
  Settings2,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  User,
  UserSearch,
  Users,
  Wallet,
  Webhook,
  Workflow,
  Wrench,
} from 'lucide-react';
import { type To } from 'react-router-dom';

import {
  type Leads_Bool_Exp,
  Leads_Status_Enum,
} from '@graphql/__generated__/graphql';
import { type Me } from '@modules/_shared/providers/AppDataProvider';
import { canAccessMls } from '@modules/_shared/utils/access';
import {
  COMPARABLE_GRID_PAGE_SIZE,
  initialComparableListings,
} from '@modules/comparable-listings/ComparableListings';
import { canReadMailbox } from '@modules/emails/threads/mailboxAccess';
import { initialLeadsListQuery } from '@modules/leads/Leads';
import { initialListingsListQuery } from '@modules/listings/Listings';
import { initialOrganisationsListQuery } from '@modules/organisations/Organisations';
import { getTodayActivityDueRange } from '@modules/performance/activities/activitySearchParams';
import { initialTeamsListQuery } from '@modules/teams/Teams';
import { isLocalhost } from '@shared/config';
import {
  MODULES_BROKERAGE,
  MODULES_CMA_REPORTS,
  MODULES_LEADS,
  MODULES_PROSPECTION_MAP,
  MODULES_VALUATION_LINK,
  MODULES_VISIBILITY,
} from '@shared/constants/module';
import { type Translate } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';
import { getModuleLabel } from '@shared/utils/module-label';
import {
  LEGACY_PROSPECTION_MAP_PATH,
  prospectionMapPath,
} from '@shared/utils/prospectionMapAccess';
import {
  canEditNotificationSettings,
  isRATenantId,
} from '@shared/utils/tenant';

type BaseLinkConfig = {
  key: string;
  label: string;
  visible: boolean;
  path: To;
  prefetch?: QueryOptions;
};

export type SidebarChildLinkConfig = BaseLinkConfig & {
  type: 'link';
  icon: LucideIcon;
};

export type SidebarSingleLinkConfig = SidebarChildLinkConfig;

export type GroupHeaderConfig = BaseLinkConfig & {
  type: 'groupHeader';
  icon: LucideIcon;
  children: SidebarChildLinkConfig[];
};

export type SidebarLinkConfig = SidebarSingleLinkConfig | GroupHeaderConfig;

export function getSidebarLinks(
  me: Me | null,
  t: Translate,
  countryCode: string,
  // The session's Hasura role (JWT default role), not a `me` flag: the
  // mailbox reads under agent / tenant_admin only.
  hasuraRole: string | null | undefined,
): SidebarLinkConfig[] {
  if (me == null) {
    return [];
  }

  const isRATenant = isRATenantId(me.tenant_id ?? '');
  const isAdmin = me.is_admin === true;
  const mailbox = canReadMailbox(hasuraRole);
  const isLeadsActive = hasActiveModuleGrant(me.module_grants, MODULES_LEADS);
  const isBrokerageActive = hasActiveModuleGrant(
    me.module_grants,
    MODULES_BROKERAGE,
  );
  const isMlsEnabled = canAccessMls(me, countryCode);
  const isCMAReportsEnabled = hasActiveModuleGrant(
    me.module_grants,
    MODULES_CMA_REPORTS,
  );
  const isVisibilityActive =
    isRATenant && hasActiveModuleGrant(me.module_grants, MODULES_VISIBILITY);
  const isValuationLinkActive =
    isRATenant &&
    hasActiveModuleGrant(me.module_grants, MODULES_VALUATION_LINK);
  // One "Prospection map" entry: the new map for those who bought it, the
  // legacy one otherwise (see prospectionMapAccess).
  const prospectionMapLink = prospectionMapPath(me);
  const canEditSettings = me.can_edit_settings === true;
  const brokerId = toGlobalId('User', me.id);

  const enquiriesSearchParams = new URLSearchParams({
    where: JSON.stringify({
      source: { _in: ['portal'] },
    }),
  });

  const leadsQueryWhere: Leads_Bool_Exp = {
    completed: { _eq: true },
    // We don't include the "None" agent filter by default for agents because it does not make sense anyway.
    ...(isAdmin
      ? {
          _not: {
            lead_agents: {
              user: {},
              lead_stage: {
                status: {
                  _in: [Leads_Status_Enum.Lost, Leads_Status_Enum.Won],
                },
              },
            },
          },
        }
      : {
          lead_agents: {
            lead_stage: { status: { _in: [Leads_Status_Enum.Active] } },
          },
        }),
  };

  const leadsSearchParams = new URLSearchParams({
    where: JSON.stringify(leadsQueryWhere),
    order_by: JSON.stringify([
      {
        requalified_or_created_at: 'desc',
      },
    ]),
  });

  const reviewsSearchParams = new URLSearchParams({
    where: JSON.stringify({
      completed: { _eq: true },
    }),
  });

  const calendarStatusQuery =
    'activityStatus_in=done&activityStatus_in=pending';

  const hasAnyModuleAccess = isAdmin || isLeadsActive || isBrokerageActive;

  const prefetchComparables = initialComparableListings(
    new URLSearchParams(),
    COMPARABLE_GRID_PAGE_SIZE,
    countryCode,
  );

  const createdAtDescSearch = new URLSearchParams({
    order_by: JSON.stringify([{ created_at: 'desc' }]),
  }).toString();

  return [
    {
      type: 'link',
      key: 'dashboard',
      label: t('Dashboard'),
      path: '/dashboard',
      icon: Compass,
      visible: true,
    },

    {
      type: 'groupHeader',
      key: 'visibility',
      label: getModuleLabel(MODULES_VISIBILITY, t),
      path: '/visibility/overview',
      icon: Eye,
      visible: isVisibilityActive,
      children: [
        {
          type: 'link',
          key: 'visibility-overview',
          icon: Gauge,
          label: t('Overview'),
          path: '/visibility/overview',
          visible: true,
        },
        {
          type: 'link',
          key: 'visibility-inbox',
          icon: Inbox,
          label: t('Inbox'),
          path: '/visibility/inbox',
          visible: true,
        },
        {
          type: 'link',
          key: 'visibility-statistics',
          icon: ChartColumn,
          label: t('Visibility statistics'),
          path: '/visibility/statistics',
          visible: true,
        },
        {
          type: 'link',
          key: 'visibility-team',
          icon: Users,
          label: t('Team'),
          path: '/visibility/team',
          visible: true,
        },
        {
          type: 'link',
          key: 'visibility-transactions',
          icon: ArrowLeftRight,
          label: t('Transactions'),
          path: '/visibility/transactions',
          visible: true,
        },
        {
          type: 'link',
          key: 'visibility-reviews',
          icon: Star,
          label: t('Reviews'),
          path: '/visibility/reviews',
          visible: true,
        },
      ],
    },

    {
      type: 'link',
      key: 'reviews',
      label: t('Reviews'),
      path: {
        pathname: '/reviews',
        search: reviewsSearchParams.toString(),
      },
      icon: Star,
      visible: isAdmin,
    },

    {
      type: 'link',
      key: 'prospection-map',
      label: getModuleLabel(MODULES_PROSPECTION_MAP, t),
      path: prospectionMapLink ?? LEGACY_PROSPECTION_MAP_PATH,
      icon: Map,
      visible: prospectionMapLink != null,
    },

    {
      type: 'groupHeader',
      key: 'cma-reports',
      label: getModuleLabel(MODULES_CMA_REPORTS, t),
      path: '/cma-reports',
      icon: FileChartColumn,
      visible: isCMAReportsEnabled,
      prefetch: prefetchComparables,
      children: [
        {
          type: 'link',
          key: 'cma-reports-list',
          icon: FileChartColumn,
          label: t('CMA reports'),
          path: '/cma-reports',
          visible: true,
        },
        {
          type: 'link',
          key: 'comparable-listings',
          icon: Scale,
          label: t('Comparable listings'),
          path: '/comparable-listings',
          visible: true,
          prefetch: prefetchComparables,
        },
      ],
    },

    {
      type: 'link',
      key: 'valuation-links',
      label: getModuleLabel(MODULES_VALUATION_LINK, t),
      path: '/valuation-links',
      icon: Magnet,
      visible: isValuationLinkActive,
    },

    {
      type: 'link',
      key: 'leads',
      label: getModuleLabel(MODULES_LEADS, t),
      path: {
        pathname: '/leads',
        search: leadsSearchParams.toString(),
      },
      icon: Target,
      visible: isLeadsActive,
      prefetch: initialLeadsListQuery(leadsSearchParams),
    },

    {
      type: 'groupHeader',
      key: 'brokerage',
      label: getModuleLabel(MODULES_BROKERAGE, t),
      path: '/listings',
      icon: Building2,
      visible: isBrokerageActive,
      prefetch: initialListingsListQuery(
        new URLSearchParams(),
        false,
        undefined,
      ),
      children: [
        {
          type: 'link',
          key: 'listings',
          icon: House,
          label: t('Listings'),
          path: '/listings',
          visible: true,
        },
        {
          type: 'link',
          key: 'developments',
          icon: Construction,
          label: t('Developments'),
          path: '/developments',
          visible: true,
        },
        {
          type: 'link',
          key: 'enquiries',
          icon: MessagesSquare,
          label: t('Enquiries'),
          path: {
            pathname: `/enquiries`,
            search: enquiriesSearchParams.toString(),
          },
          visible: true,
        },
        {
          type: 'link',
          key: 'property-searches',
          icon: Search,
          label: t('Property searches'),
          path: '/property-searches',
          visible: true,
        },
        {
          type: 'link',
          key: 'offers',
          icon: HandCoins,
          label: t('Offers'),
          path: '/offers',
          visible: true,
        },
        {
          type: 'link',
          key: 'transactions',
          icon: Handshake,
          label: t('Transactions'),
          path: '/transactions',
          visible: true,
        },
        {
          type: 'link',
          key: 'buyer-leads',
          icon: UserSearch,
          label: t('Buyer leads'),
          path: {
            pathname: '/v2/buyer-leads',
            search: createdAtDescSearch,
          },
          visible: isAdmin,
        },
        {
          type: 'link',
          key: 'mls',
          icon: Network,
          label: t('MLS'),
          path: '/mls',
          visible: isMlsEnabled,
        },
      ],
    },

    {
      type: 'groupHeader',
      key: 'contacts',
      label: t('contacts'),
      path: '/users',
      icon: Users,
      visible: hasAnyModuleAccess,
      children: [
        {
          type: 'link',
          key: 'users',
          icon: User,
          label: t('users'),
          path: '/users',
          visible: true,
        },
        {
          type: 'link',
          key: 'teams',
          icon: Building,
          label: t('Agencies / Teams'),
          path: '/teams',
          visible: isAdmin,
          prefetch: initialTeamsListQuery(new URLSearchParams()),
        },
        {
          type: 'link',
          key: 'organisations',
          icon: Landmark,
          label: t('organisations'),
          path: '/organisations',
          visible: isAdmin,
          prefetch: initialOrganisationsListQuery(new URLSearchParams()),
        },
      ],
    },

    {
      type: 'link',
      key: 'calendar',
      label: t('calendar'),
      path: isAdmin
        ? `/calendar?activityType_in=visit&${calendarStatusQuery}`
        : `/calendar?activityType_in=call&activityType_in=visit&activityType_in=task&${calendarStatusQuery}&brokerId_in=${brokerId}`,
      icon: CalendarDays,
      visible: hasAnyModuleAccess,
    },

    {
      type: 'groupHeader',
      key: 'email',
      label: t('email'),
      path: mailbox ? '/emails/inbox' : '/emails/templates',
      icon: Mail,
      visible: hasAnyModuleAccess,
      children: [
        {
          type: 'link',
          key: 'email-inbox',
          icon: Inbox,
          label: t('inbox'),
          path: '/emails/inbox',
          visible: mailbox,
        },
        {
          type: 'link',
          key: 'email-sent',
          icon: Send,
          label: t('sent'),
          path: '/emails/sent',
          visible: mailbox,
        },
        {
          type: 'link',
          key: 'email-templates',
          icon: FileText,
          label: t('templates'),
          path: {
            pathname: '/emails/templates',
            // Default sort, carried in the URL so the list opens newest-first
            // (RaDataGrid header clicks then rewrite this param).
            search: new URLSearchParams({
              order_by: '[{ "created_at": "desc" }]',
            }).toString(),
          },
          visible: true,
        },
        {
          type: 'link',
          key: 'email-campaigns',
          icon: Megaphone,
          label: t('massMail'),
          path: '/emails/campaigns',
          visible: isBrokerageActive || isAdmin,
        },
        {
          type: 'link',
          key: 'email-preferences',
          icon: SlidersHorizontal,
          label: t('settings'),
          path: '/emails/preferences',
          visible: true,
        },
      ],
    },

    {
      type: 'groupHeader',
      key: 'performance',
      label: t('performance'),
      path: '/performance/stats',
      icon: ChartLine,
      visible: isAdmin || isLeadsActive,
      children: [
        {
          type: 'link',
          key: 'performance-stats',
          icon: ChartColumn,
          label: t('stats'),
          path: '/performance/stats',
          visible: isLeadsActive,
        },
        {
          type: 'link',
          key: 'performance-agent-stats',
          icon: TrendingUp,
          label: t('agentStats'),
          path: {
            pathname: `/performance/agent-stats`,
            search: new URLSearchParams({
              startDate: subYears(Date.now(), 1).getTime().toString(),
              endDate: Date.now().toString(),
              sortBy: 'transactions_volume',
              sortDirection: 'desc',
            }).toString(),
          },
          visible: isLeadsActive,
        },
        {
          type: 'link',
          key: 'performance-activities',
          icon: ListChecks,
          label: t('activities'),
          path: {
            pathname: `/performance/activities`,
            search: new URLSearchParams({
              where: JSON.stringify({
                consolidated_due_at: getTodayActivityDueRange(Date.now()),
                done: { _eq: false },
              }),
            }).toString(),
          },
          visible: true,
        },
      ],
    },

    {
      type: 'groupHeader',
      key: 'saas',
      label: 'SaaS',
      path: '/subscriptions',
      icon: CreditCard,
      visible: isAdmin && isRATenant,
      children: [
        {
          type: 'link',
          key: 'subscriptions',
          icon: Repeat,
          label: t('subscriptions'),
          path: '/subscriptions',
          visible: true,
        },
        {
          type: 'link',
          key: 'stripe-subscriptions',
          icon: Wallet,
          label: t('stripeSubscriptions'),
          path: '/stripe-subscriptions',
          visible: true,
        },
        {
          type: 'link',
          key: 'invoices',
          icon: Receipt,
          label: t('invoices'),
          path: '/invoices',
          visible: true,
        },
        {
          type: 'link',
          key: 'checkout-pages',
          icon: ShoppingCart,
          label: t('checkoutPages'),
          path: '/checkout-pages',
          visible: true,
        },
      ],
    },

    {
      type: 'groupHeader',
      key: 'settings',
      label: t('settings'),
      path: '/settings/general',
      icon: Settings,
      visible: canEditSettings,
      children: [
        {
          type: 'link',
          key: 'settings-general',
          icon: Settings2,
          label: t('general'),
          path: '/settings/general',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-dictionaries',
          icon: BookOpen,
          label: t('dictionaries'),
          path: '/settings/dictionaries',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-pipelines',
          icon: Workflow,
          label: t('pipelines'),
          path: '/settings/pipelines',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-portals',
          icon: Globe,
          label: t('portals'),
          path: '/settings/portals',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-doc-templates',
          icon: FileStack,
          label: t('templates'),
          path: '/settings/doc-templates',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-webhook-subscriptions',
          icon: Webhook,
          label: t('webhooks'),
          path: '/settings/webhook-subscriptions',
          visible: isAdmin,
        },
        {
          type: 'link',
          key: 'settings-notifications',
          icon: Bell,
          label: t('Notifications'),
          path: '/settings/notifications',
          visible: canEditNotificationSettings(me),
        },
        {
          type: 'link',
          key: 'settings-product-updates',
          icon: Sparkles,
          label: t('Product updates'),
          path: '/settings/product-updates',
          // RealAdvisor staff only, like the AI agent admin pages.
          visible: isAdmin && isRATenant,
        },
        {
          type: 'link',
          key: 'settings-developer',
          icon: Code,
          label: t('Developer'),
          path: '/settings/developer',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-developer-tools',
          icon: Wrench,
          label: t('Developer Tools'),
          path: '/settings/developer-tools',
          visible: isLocalhost,
        },
      ],
    },

    // Admin-only AI agent controls. Pure navigation: the pages live under the
    // settings module/routes; this group just surfaces them at the top level.
    {
      type: 'groupHeader',
      key: 'ai-agent',
      label: t('AI Agent'),
      path: '/settings/chat-conversations',
      icon: Bot,
      visible: isAdmin && isRATenant,
      children: [
        {
          type: 'link',
          key: 'settings-chat-conversations',
          icon: MessagesSquare,
          label: t('AI Chats'),
          path: '/settings/chat-conversations',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-chat-models',
          icon: SlidersHorizontal,
          label: t('AI Models'),
          path: '/settings/chat-models',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-chat-consumption',
          icon: ChartLine,
          label: t('Consumption'),
          path: '/settings/chat-consumption',
          visible: true,
        },
        {
          type: 'link',
          key: 'settings-chat-skills',
          icon: NotebookText,
          label: t('AI Skills'),
          path: '/settings/chat-skills',
          visible: true,
        },
      ],
    },

    {
      type: 'groupHeader',
      key: 'v2',
      label: t('v2'),
      path: {
        pathname: `/v2/buyer-leads`,
        search: createdAtDescSearch,
      },
      icon: FlaskConical,
      // Same rule as the /v2 routes (App.tsx): RealAdvisor admins only.
      visible: isAdmin && isRATenant,
      children: [
        {
          type: 'link',
          key: 'v2-buyer-leads',
          icon: UserSearch,
          label: t('Buyer leads'),
          path: {
            pathname: `/v2/buyer-leads`,
            search: createdAtDescSearch,
          },
          visible: true,
        },
        {
          type: 'link',
          key: 'v2-mls',
          icon: Network,
          label: t('MLS'),
          path: '/v2/mls',
          visible: isMlsEnabled,
        },
        {
          type: 'link',
          key: 'v2-performance-leads',
          icon: ChartLine,
          label: t('Leads Statistics'),
          path: '/v2/performance/leads',
          visible: true,
        },
        {
          type: 'link',
          key: 'v2-users',
          icon: Users,
          label: t('Users'),
          path: {
            pathname: `/v2/users`,
            search: createdAtDescSearch,
          },
          visible: true,
        },
        {
          type: 'link',
          key: 'v2-tenant',
          icon: Building,
          label: t('Tenant Settings'),
          path: '/v2/tenant',
          visible: true,
        },
        {
          type: 'link',
          key: 'v2-subscriptions',
          icon: CreditCard,
          label: t('Subscriptions'),
          path: {
            pathname: `/v2/subscriptions`,
            search: createdAtDescSearch,
          },
          visible: true,
        },
      ],
    },

    // Agent Academy (docs) — no longer admin-only; available to every CRM user,
    // on every tenant. The `/docs` route in `App.tsx` is likewise ungated. The
    // content, the `crm-chat-api` `/docs/search`/`/docs/read` endpoints, and the
    // published index are all untenanted, so this is a deliberately public
    // help/training surface rather than a tenant-scoped one.
    {
      type: 'link',
      key: 'agent-academy',
      label: t('Agent Academy'),
      path: '/docs',
      icon: GraduationCap,
      visible: true,
    },
  ];
}
